import {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {KTIcon} from '../../../../helpers'
import {axiosClientAuth} from '../../../../../app/utils'
import Swal from 'sweetalert2'
import {
  buildPermissionRowsFromCatalog,
  getAdminPermissionsCatalog,
  getAdminRoleById,
  mapAdminRoleToListRow,
  slugifyRoleSlug,
  updateAdminRole,
} from '../../../../../app/modules/auth/core/_requests'

const ACCENT = '#064e3b' // Forest Green
const BORDER = '#d1fae5' // Light emerald border

type ModalEditRoleProps = {
  show: boolean
  setShow: (v: boolean) => void
  curr: any
  onSuccess?: () => void
  onDelete?: () => void
}

const ModalEditRole = ({show, setShow, curr, onSuccess, onDelete}: ModalEditRoleProps) => {
  const [roleName, setRoleName] = useState(curr?.name || '')
  const [roleDescription, setRoleDescription] = useState(curr?.description || '')
  const [permissions, setPermissons] = useState<any>([])
  const [errorMsg, setErrorMsg] = useState('')
  const [resolvedRow, setResolvedRow] = useState<any>(null)
  const [submitting, setSubmitting] = useState(false)
  const [checkbox, setCheckbox] = useState(false)

  const resetForm = () => {
    setErrorMsg('')
    const base = resolvedRow ?? curr
    setRoleName(base?.name || '')
    setRoleDescription(base?.description || '')
    const tempData = permissions?.map((ele: any) => {
      const existing = base?.group_permissions?.filter(
        (item: any) =>
          item?.permission?.uuid == ele?.uuid ||
          item?.permission?.name === ele?.name ||
          item?.permission?.name === ele?.uuid
      )
      return existing?.length
        ? {
            ...ele,
            can_read: existing[0]?.can_read,
            can_write: existing[0]?.can_write,
            can_edit: existing[0]?.can_edit,
          }
        : {...ele, can_read: false, can_write: false, can_edit: false}
    })
    setCheckbox(false)
    setPermissons(tempData)
  }

  useEffect(() => {
    if (!show) {
      setResolvedRow(null)
      return
    }
    if (!curr) return
    let cancelled = false
    ;(async () => {
      try {
        let row = curr
        try {
          const role = await getAdminRoleById(curr.uuid)
          if (cancelled) return
          row = mapAdminRoleToListRow(role)
        } catch {
          /* legacy row or API error */
        }
        if (cancelled) return
        setResolvedRow(row)
        setRoleName(row.name || '')
        setRoleDescription(row.description || '')
        const powers = await getAdminPermissionsCatalog()
        if (cancelled) return
        setPermissons(
          buildPermissionRowsFromCatalog(powers, (code) => {
            const gp = row.group_permissions?.find(
              (item: any) =>
                item?.permission?.name === code || String(item?.permission?.uuid) === code
            )
            if (!gp) return undefined
            return {
              can_read: gp.can_read ?? true,
              can_write: gp.can_write ?? true,
              can_edit: gp.can_edit ?? true,
            }
          })
        )
      } catch (error) {
        console.log(error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [show, curr?.uuid])

  const onCheckBoxClick = (event, type, element) => {
    const newPermissions: any = permissions.map((ele: any) => {
      if (ele.uuid == element.uuid) {
        return {...ele, [type]: event.target.checked}
      }
      return ele
    })
    setPermissons(newPermissions)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!roleName.trim() || !roleDescription.trim()) {
      setErrorMsg('Name and Description are required !!!')
      return
    }
    if (
      permissions.filter((ele: any) => ele.can_read || ele.can_write || ele.can_edit)?.length <= 0
    ) {
      setErrorMsg('Atleast 1 role permission is required !!!')
      return
    }

    const permission_codes = permissions
      .filter((ele: any) => ele.can_read || ele.can_write || ele.can_edit)
      .map((p: any) => p.uuid)

    const rowForApi = resolvedRow ?? curr
    const useFastApi = Array.isArray(rowForApi?.permission_codes)

    setSubmitting(true)
    try {
      if (useFastApi) {
        await updateAdminRole(curr?.uuid, {
          slug: slugifyRoleSlug(roleName),
          display_name: roleName.trim(),
          description: roleDescription.trim(),
          permission_codes,
        })
      } else {
        const mData: any = {
          uuid: curr?.uuid,
          name: roleName,
          permissions: permissions,
          description: roleDescription,
        }
        await axiosClientAuth.patch('/auth/api-permission-group', mData)
      }
      await Swal.fire({
        titleText: 'Success!',
        text: 'Role Updated Successfully',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
      setShow(false)
      onSuccess?.()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong!'
      Swal.fire('Error!', typeof msg === 'string' ? msg : 'Something went wrong!', 'error')
      console.log(err)
    } finally {
      setSubmitting(false)
    }
  }

  const selectAll = (e) => {
    setCheckbox(e.target.checked)
    const temp: any = permissions.map((item: any) => {
      return {
        ...item,
        can_read: e.target.checked,
        can_write: e.target.checked,
        can_edit: e.target.checked,
      }
    })
    setPermissons(temp)
  }

  const blogPermissions = permissions?.filter((p: any) => p.label?.toLowerCase().includes('blog') || p.name?.toLowerCase().includes('blog')) || []
  const otherPermissions = permissions?.filter((p: any) => !p.label?.toLowerCase().includes('blog') && !p.name?.toLowerCase().includes('blog')) || []

  const allBlogSelected = blogPermissions.length > 0 && blogPermissions.every((p: any) => p.can_read && p.can_write && p.can_edit)

  const selectAllBlog = (e: any) => {
    const temp = permissions.map((item: any) => {
      if (item.label?.toLowerCase().includes('blog') || item.name?.toLowerCase().includes('blog')) {
        return {
          ...item,
          can_read: e.target.checked,
          can_write: e.target.checked,
          can_edit: e.target.checked,
        }
      }
      return item
    })
    setPermissons(temp)
  }

  const handleClose = () => {
    setShow(false)
    resetForm()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='lg'
      className='modal-edit-role'
      contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
    >
      <div className='modal-content border-0'>
        <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white'>
          <div>
            <h2 className='fw-bold fs-3 mb-1' style={{color: ACCENT}}>
              Edit Role
            </h2>
            <p className='text-gray-600 fs-7 mb-0 fw-semibold'>
              Update role details and permissions
            </p>
          </div>
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light rounded-circle'
            onClick={handleClose}
            aria-label='Close'
          >
            <KTIcon iconName='cross' className='fs-2 text-gray-600' />
          </button>
        </div>

        <div className='modal-body px-8 pt-4 pb-2 scroll-y' style={{maxHeight: '70vh'}}>
          <form className='form' onSubmit={onSubmit}>
            {errorMsg && (
              <div className='mb-8 alert alert-dismissible bg-light-danger border border-danger border-dashed d-flex flex-column flex-sm-row p-5 rounded-3'>
                <KTIcon iconName='information-5' className='fs-2hx text-danger me-4 mb-5 mb-sm-0' />
                <div className='d-flex flex-column pe-0 pe-sm-10'>
                  <h5 className='mb-1'>Required Fields</h5>
                  <span>{errorMsg}</span>
                </div>
              </div>
            )}

            <div className='mb-10'>
              <h3 className='fw-bold fs-5 mb-5' style={{color: ACCENT}}>
                Role Details
              </h3>
              <div className='row g-5'>
                <div className='col-md-6 fv-row'>
                  <label className='form-label fw-semibold text-gray-800 fs-7 mb-2'>
                    Role Name <span className='text-danger'>*</span>
                  </label>
                  <input
                    className='form-control form-control-solid rounded-3 border-0 py-3'
                    placeholder='e.g. Administrator'
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                  />
                </div>
                <div className='col-md-6 fv-row'>
                  <label className='form-label fw-semibold text-gray-800 fs-7 mb-2'>
                    Description <span className='text-danger'>*</span>
                  </label>
                  <input
                    className='form-control form-control-solid rounded-3 border-0 py-3'
                    placeholder='Briefly describe this role'
                    value={roleDescription}
                    onChange={(e) => setRoleDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className='mb-4'>
              <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4'>
                <h3 className='fw-bold fs-5 mb-0' style={{color: ACCENT}}>
                  Role Permissions
                </h3>
                <div className='form-check form-check-custom form-check-solid form-check-sm'>
                  <input
                    className='form-check-input rounded-1'
                    type='checkbox'
                    id='select_all_perms'
                    checked={checkbox}
                    onChange={selectAll}
                  />
                  <label
                    className='form-check-label fw-bold text-gray-700 fs-7 ms-2'
                    htmlFor='select_all_perms'
                  >
                    Select All Access
                  </label>
                </div>
              </div>
              <p className='text-gray-600 fs-7 mb-6'>
                Assign specific capabilities for each system module.
              </p>

              <div className='d-flex flex-column gap-3 mb-5'>
                {otherPermissions?.map((permission: any) => (
                  <div
                    key={permission.uuid}
                    className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-4 py-4 px-5 rounded-4 bg-white border border-gray-100 shadow-sm transition-all hover-elevated-sm'
                  >
                    <div className='min-w-0'>
                      <span className='text-gray-900 fw-bold fs-6 d-block mb-1'>
                        {permission.label || permission.name}
                      </span>
                      <span className='text-gray-500 fs-8 font-monospace'>{permission.name}</span>
                    </div>

                    <div className='d-flex align-items-center gap-4 flex-wrap'>
                      {[
                        {key: 'can_read', label: 'Read'},
                        {key: 'can_write', label: 'Create'},
                        {key: 'can_edit', label: 'Modify'},
                      ].map((type) => (
                        <label
                          key={type.key}
                          className='form-check form-check-sm form-check-custom form-check-solid gap-2 cursor-pointer'
                        >
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={permission[type.key]}
                            onChange={(e) => onCheckBoxClick(e, type.key, permission)}
                          />
                          <span className='form-check-label fw-semibold text-gray-700 fs-7'>
                            {type.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {blogPermissions?.length > 0 && (
                <div className='mt-6 p-5 rounded-4' style={{backgroundColor: '#f8fafc', border: '1px solid #e2e8f0'}}>
                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-5 pb-4 border-bottom border-gray-200'>
                    <h3 className='fw-bold fs-5 mb-0' style={{color: ACCENT}}>
                      Blog Access
                    </h3>
                    <div className='form-check form-check-custom form-check-solid form-check-sm'>
                      <input
                        className='form-check-input rounded-1 border-primary'
                        type='checkbox'
                        id='select_all_blog_perms'
                        checked={allBlogSelected}
                        onChange={selectAllBlog}
                      />
                      <label
                        className='form-check-label fw-bold text-gray-700 fs-7 ms-2'
                        htmlFor='select_all_blog_perms'
                      >
                        Select All Blog Access
                      </label>
                    </div>
                  </div>
                  
                  <div className='d-flex flex-column gap-3'>
                    {blogPermissions.map((permission: any) => (
                      <div
                        key={permission.uuid}
                        className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-4 py-4 px-5 rounded-4 bg-white border border-gray-100 shadow-sm transition-all hover-elevated-sm'
                      >
                        <div className='min-w-0'>
                          <span className='text-gray-900 fw-bold fs-6 d-block mb-1'>
                            {permission.label || permission.name}
                          </span>
                          <span className='text-gray-500 fs-8 font-monospace'>{permission.name}</span>
                        </div>

                        <div className='d-flex align-items-center gap-4 flex-wrap'>
                          {[
                            {key: 'can_read', label: 'Read'},
                            {key: 'can_write', label: 'Create'},
                            {key: 'can_edit', label: 'Modify'},
                          ].map((type) => (
                            <label
                              key={type.key}
                              className='form-check form-check-sm form-check-custom form-check-solid gap-2 cursor-pointer'
                            >
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={permission[type.key]}
                                onChange={(e) => onCheckBoxClick(e, type.key, permission)}
                              />
                              <span className='form-check-label fw-semibold text-gray-700 fs-7'>
                                {type.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className='d-none'>
              <button type='submit' id='modal_edit_role_submit_hidden' />
            </div>
          </form>
        </div>

        <div className='modal-footer border-0 bg-white pt-4 px-8 pb-8 d-flex flex-wrap justify-content-between align-items-center gap-3'>
          <div>
            {onDelete && (
              <button
                type='button'
                className='btn btn-light-danger border border-danger border-opacity-10 fw-bold px-6 rounded-3 transition-all'
                onClick={onDelete}
              >
                <i className='fas fa-trash-alt me-2 fs-7' />
                Delete Role
              </button>
            )}
          </div>
          <div className='d-flex gap-3'>
            <button
              type='button'
              className='btn btn-light fw-bold px-6 rounded-3'
              onClick={handleClose}
            >
              Discard
            </button>
            <button
              type='button'
              className='btn btn-primary px-10 rounded-3 fw-bold'
              disabled={submitting}
              onClick={onSubmit}
            >
              {!submitting ? (
                'Save Changes'
              ) : (
                <span className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-sm me-2' />
                  Saving...
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ModalEditRole
